/**
 * A console trace of what the snapshot stream says about entities leaving.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * Since the delta landed, a frame carries only what changed and `gone` is the **only** thing
 * that removes an entity. That turned a whole family of faults silent and identical on screen:
 * a monster that dies and stays, standing at the health it had, unkillable because the server
 * has long since forgotten it. Three different causes produce exactly that picture — the server
 * losing its record of what the client holds, the server forgetting to re-describe something,
 * and the client dropping a removal before the renderer drains it — and none of them logs
 * anything.
 *
 * The decisive question is one the wire can answer and a screenshot cannot: **was this entity's
 * death followed by its id in `gone`?** So this watches for exactly that and complains when the
 * answer is no.
 *
 * ── The trap it was written around ───────────────────────────────────────────
 *
 * **`EVENT_ENTITY_DIED` used to arrive for monsters in other people's zones.** `World.events`
 * was one world-wide list copied into every instance's payload, so counting deaths against
 * removals compared somebody else's kills with your own and always looked broken — it cost a
 * whole measurement. Events are per instance now, and the filter stays anyway: only entities
 * this client is actually holding are judged, and membership is tracked here from the same
 * frames rather than read from the store. A diagnostic that trusts the layer it is diagnosing
 * agrees with the bug.
 *
 * ── Using it ─────────────────────────────────────────────────────────────────
 *
 *   ?wirelog=1    trace removals: one line per death, and a loud line for one never announced
 *
 * Off unless asked for, and it allocates nothing when off.
 */

/** How many frames to wait before calling a death unannounced. 30 Hz, so this is ~2 seconds. */
const GRACE_FRAMES = 60;

interface Watched {
  id: string;
  diedAtFrame: number;
  hpAtDeath: number | undefined;
  /** How many non-empty `gone` lists have arrived since — a removal stream that is running
   *  and simply never names this id says something different from one that is dead. */
  goneListsSince: number;
}

export interface WireLogFrame {
  entities: readonly { id: string; hp?: number; name?: string }[];
  gone?: readonly string[];
  /**
   * `unknown`, because the store holds its event list loosely and this must not force a
   * shape on it — a diagnostic that changes the types it is diagnosing is worse than none.
   * Narrowed per event below.
   */
  events?: readonly unknown[];
}

function enabled(): boolean {
  if (typeof window === 'undefined') return false;
  const v = new URLSearchParams(window.location.search).get('wirelog');
  return v === '1' || v === 'true' || v === 'on';
}

/**
 * Watches the snapshot stream and reports removals that never came.
 *
 * Deliberately keeps its own membership set. Reading the store's would make the trace agree
 * with the bug whenever the bug is in the store.
 */
export class WireLog {
  private readonly on = enabled();
  private readonly held = new Set<string>();
  private readonly watching: Watched[] = [];
  private readonly lastHp = new Map<string, number>();
  private frame = 0;
  private goneLists = 0;
  private goneIds = 0;

  /** True when the trace is switched on, so callers can skip the work entirely. */
  get active(): boolean {
    return this.on;
  }

  /** A zone change resets everything: the ids do not survive it. */
  reset(): void {
    if (!this.on) return;
    this.held.clear();
    this.watching.length = 0;
    this.lastHp.clear();
    this.goneLists = 0;
    this.goneIds = 0;
  }

  observe(payload: WireLogFrame): void {
    if (!this.on) return;
    this.frame += 1;

    for (const e of payload.entities) {
      this.held.add(e.id);
      if (typeof e.hp === 'number') this.lastHp.set(e.id, e.hp);
    }

    for (const raw of payload.events ?? []) {
      const ev = raw as { type?: unknown; entityId?: unknown };
      if (ev.type !== 'EVENT_ENTITY_DIED' || typeof ev.entityId !== 'string') continue;
      // **Only what this client holds.** Deaths in other zones arrive here too.
      if (!this.held.has(ev.entityId)) continue;
      if (this.watching.some((w) => w.id === ev.entityId)) continue;
      this.watching.push({
        id: ev.entityId,
        diedAtFrame: this.frame,
        hpAtDeath: this.lastHp.get(ev.entityId),
        goneListsSince: 0,
      });
      console.info(
        `[wirelog] died: ${ev.entityId} (last hp seen ${this.lastHp.get(ev.entityId) ?? '?'})`,
      );
    }

    const gone = payload.gone ?? [];
    if (gone.length > 0) {
      this.goneLists += 1;
      this.goneIds += gone.length;
      for (const w of this.watching) w.goneListsSince += 1;
      console.info(`[wirelog] gone: [${gone.join(', ')}]`);
    }
    for (const id of gone) {
      this.held.delete(id);
      this.lastHp.delete(id);
      const i = this.watching.findIndex((w) => w.id === id);
      if (i >= 0) {
        const w = this.watching[i]!;
        console.info(`[wirelog] removed: ${id}, ${this.frame - w.diedAtFrame} frame(s) after it died`);
        this.watching.splice(i, 1);
      }
    }

    // Anything still waiting well past its death is the fault this file exists for.
    for (let i = this.watching.length - 1; i >= 0; i--) {
      const w = this.watching[i]!;
      if (this.frame - w.diedAtFrame < GRACE_FRAMES) continue;
      console.error(
        `[wirelog] NEVER REMOVED: ${w.id} died ${this.frame - w.diedAtFrame} frames ago and its id ` +
          `has not appeared in \`gone\`. The client is still holding it at hp ${w.hpAtDeath ?? '?'}. ` +
          `Since it died the server sent ${w.goneListsSince} removal list(s) naming other ids ` +
          `(${this.goneIds} ids over ${this.goneLists} list(s) this zone). ` +
          `**Zero here means the removal stream is dead; a non-zero count means it is running ` +
          `and simply never names this monster** — which is the difference between the two halves ` +
          `of the system, and the whole reason this line exists.`,
      );
      this.watching.splice(i, 1);
    }
  }

  /** How many deaths are still waiting for their removal. For tests. */
  get pending(): number {
    return this.watching.length;
  }
}
